import type { GalleryItem } from '@mtc/shared';
import { useEffect, useRef, useState } from 'react';

type Props = {
  images: GalleryItem[];
  /** Max number of images to cycle through. */
  limit?: number;
  /** Auto-advance interval in ms. */
  intervalMs?: number;
};

/**
 * Hero photo carousel — single visible image with a smooth cross-fade between
 * slides, auto-advancing on a timer. Pauses while the cursor is over the card
 * so visitors can take a moment with one frame. Renders nothing when there
 * are no images, so the layout never shows an empty placeholder slot.
 */
export function HeroCarousel({ images, limit = 10, intervalMs = 4000 }: Props) {
  // The hero is an auto-fading cross-dissolve — playing a `<video>` here is
  // distracting and wastes bandwidth. Show photos only; videos still appear
  // in /galerie and on the formation detail page where they can be played
  // with proper controls.
  const slides = images.filter((g) => g.kind !== 'video').slice(0, limit);
  const [index, setIndex] = useState(0);
  const hoverRef = useRef(false);

  useEffect(() => {
    if (slides.length <= 1) return;
    const id = window.setInterval(() => {
      if (hoverRef.current) return;
      setIndex((i) => (i + 1) % slides.length);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [slides.length, intervalMs]);

  if (slides.length === 0) return null;

  return (
    <div
      className="md-hero-carousel"
      onMouseEnter={() => (hoverRef.current = true)}
      onMouseLeave={() => (hoverRef.current = false)}
      aria-roledescription="carousel"
      aria-label="Photos des sessions MTC"
    >
      <div className="md-hero-carousel-frame">
        {slides.map((s, i) => (
          <img
            key={s.id}
            src={s.url}
            alt={s.caption ?? ''}
            loading={i === 0 ? 'eager' : 'lazy'}
            className={`md-hero-carousel-img ${i === index ? 'is-on' : ''}`}
            aria-hidden={i !== index}
          />
        ))}
      </div>
      {slides.length > 1 && (
        <div className="md-hero-carousel-dots" role="tablist" aria-label="Slides">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`md-hero-carousel-dot ${i === index ? 'is-on' : ''}`}
              onClick={() => setIndex(i)}
              aria-label={`Slide ${i + 1}`}
              aria-selected={i === index}
              role="tab"
            />
          ))}
        </div>
      )}
    </div>
  );
}
